/**
 * Prílohy správ AI asistenta — jediný zdroj pravdy pre API (validácia uploadu, čítanie príloh
 * e-mailov) aj klientov (výber súboru, doplnenie MIME). Typy = čo OpenAI Responses API vie
 * prečítať: obrázky ako `input_image`, PDF a Office ako `input_file`. Starý binárny Office
 * (.doc/.xls/.ppt) ani HEIC model nečíta.
 */
export const ASSISTANT_ATTACHMENT_MIME_TYPES: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
}

/** 20 MB na súbor — rovnako ako dokumenty a prílohy e-mailov. */
export const ASSISTANT_ATTACHMENT_MAX_BYTES = 20 * 1024 * 1024

export const ASSISTANT_ATTACHMENTS_PER_MESSAGE = 5

/**
 * Súčet príloh v jednom chate. OpenAI berie najviac 50 MB súborov na request a pri každom
 * turne sa posielajú všetky prílohy konverzácie znova.
 */
export const ASSISTANT_CHAT_ATTACHMENTS_MAX_BYTES = 40 * 1024 * 1024

/** `accept` pre výber súboru v prehliadači. */
export const ASSISTANT_ATTACHMENT_ACCEPT = [
  ...Object.keys(ASSISTANT_ATTACHMENT_MIME_TYPES),
  ...Object.values(ASSISTANT_ATTACHMENT_MIME_TYPES).map((ext) => `.${ext}`),
  '.jpeg',
].join(',')

const MIME_BY_EXTENSION: Record<string, string> = {
  ...Object.fromEntries(Object.entries(ASSISTANT_ATTACHMENT_MIME_TYPES).map(([mime, ext]) => [ext, mime])),
  jpeg: 'image/jpeg',
}

/**
 * MIME prílohy: deklarovaný typ, ak je podporovaný, inak podľa prípony. Android DocumentPicker
 * a niektoré prehliadače posielajú pri Office súboroch prázdny typ alebo `application/octet-stream`.
 */
export function assistantAttachmentMime(filename: string, declared?: string | null): string {
  if (declared && ASSISTANT_ATTACHMENT_MIME_TYPES[declared]) return declared
  const ext = filename.toLowerCase().split('.').pop() ?? ''
  return MIME_BY_EXTENSION[ext] ?? declared ?? 'application/octet-stream'
}

export function isAssistantImageMime(mimeType: string): boolean {
  return mimeType.startsWith('image/')
}

/** Príloha, ako ju zobrazujú klienti — `url` je krátkodobá presigned URL. */
export interface AssistantAttachmentView {
  id: string
  filename: string
  mimeType: string
  sizeBytes: number
  url: string
}
